import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate, useParams } from 'react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Product } from '../../models/Product';
import { useLazyQuery } from '@apollo/client/react';
import { GET_PRODUCT_BY_ID_QUERY } from '../../queries/ProductQueries';
import PageContainer from '../layout/PageContainer';
import { Alert, IconButton, Tooltip } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridEventListener,
  gridClasses,
} from '@mui/x-data-grid';
import { PackagingType } from '../../models/ProductInventoryItem';
import { arrayToMap } from '../../utilities/MapUtilities';

const INITIAL_PAGE_SIZE = 100;

export default function ProductInventoryItemList() {
  const { productId } = useParams();
  const navigate = useNavigate();

  const [getProductById] = useLazyQuery(
    GET_PRODUCT_BY_ID_QUERY,
    {
      fetchPolicy: 'cache-and-network'
    }
  );

  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadData = useCallback(async () => {
    setError(null);

    const id = +(productId || '0');

    if (id > 0) {
      setIsLoading(true);

      try {
        const { data, error: getProductByIdError } = await getProductById(
          { variables: { id } }
        );

        setIsLoading(false);
        setError(null);

        if (getProductByIdError) {
          setError(getProductByIdError);
        }

        if (data?.product) {
          setProduct(data.product);
        } else {
          setError({ message: 'No product found' } as Error);
        }
      } catch (showDataError) {
        setError(showDataError as Error);
      }
    }
    setIsLoading(false);
  }, [getProductById, productId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleBack = useCallback(() => {
    navigate(`/dashboard/products/${productId}`);
  }, [navigate, productId]);

  const handleRefresh = useCallback(() => {
    if (!isLoading) {
      loadData();
    }
  }, [isLoading, loadData]);

  const handleRowClick = useCallback<GridEventListener<'rowClick'>>(
    ({ row }) => {
      navigate(`/dashboard/products/${productId}/product-inventory-items/${row.id}/edit`);
    },
    [navigate, productId],
  );

  const handleCreateClick = useCallback(() => {
    navigate(`/dashboard/products/${productId}/product-inventory-items/new`);
  }, [navigate, productId]);

  const inventoryQuantityOnHand = useMemo(() => {
    return arrayToMap<PackagingType, number>(
      (product?.totalQuantityOnHand || []));
  }, [product?.totalQuantityOnHand]);

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'packagingType',
        headerName: 'Packaging Type',
        flex: 1,
        display: 'flex',
        valueFormatter: (value) => value
      },
      {
        field: 'batchName',
        headerName: 'Batch Lot Number',
        flex: 1,
        display: 'flex',
        valueGetter: (_, row) => row.batch?.lotNumber || 'N/A'
      },
      {
        field: 'quantityOnHand',
        headerName: 'Quantity',
        type: 'number',
        flex: .50,
        display: 'flex',
        valueGetter: (_, row) =>
          (row.packagingType === PackagingType.JAR || row.packagingType === PackagingType.POUCH) ?
            (row.quantityOnHand || 0).toFixed(0) :
            (row.quantityOnHand || 0).toFixed(3)
      }
    ],
    [],
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          m: 1,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <Alert severity="error">{error.message}</Alert>
      </Box>
    );
  }

  const pageTitle = `${product?.name} Inventory`;

  return (
    <PageContainer
      title={pageTitle}
      breadcrumbs={[
        { title: 'Products', path: '/dashboard/products' },
        { title: `${product?.name}`, path: `/dashboard/products/${product?.id}` },
        { title: 'Inventory' }
      ]}
      actions={
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title="Reload data" placement="right" enterDelay={1000}>
            <div>
              <IconButton size="small" aria-label="refresh" onClick={handleRefresh}>
                <RefreshIcon />
              </IconButton>
            </div>
          </Tooltip>
          <Button
            variant="contained"
            onClick={handleCreateClick}
            startIcon={<AddIcon />}
          >
            Create
          </Button>
        </Stack>
      }>
      <Box
        sx={{ width: '100%' }}
      >
        <Grid container spacing={1} sx={{ mb: 2, width: '100%' }}>
          <Box sx={{ height: '50vh', width: '100%' }}>
            <DataGrid
              density="compact"
              rows={product?.productInventoryItems}
              columns={columns}
              loading={isLoading}
              pageSizeOptions={[INITIAL_PAGE_SIZE]}
              getRowHeight={() => 'auto'}
              onRowClick={handleRowClick}
              disableRowSelectionOnClick
              sx={[
                () => ({
                  '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '8px' },
                  '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '15px' },
                  '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: '22px' },
                  [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
                    outline: 'transparent',
                  },
                  [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
                  {
                    outline: 'none',
                  },
                  [`& .${gridClasses.row}:hover`]: {
                    cursor: 'pointer',
                  }
                }),
              ]}
              slotProps={{
                loadingOverlay: {
                  variant: 'circular-progress',
                  noRowsVariant: 'circular-progress',
                },
                baseIconButton: {
                  size: 'small',
                },
              }}
            />
          </Box>
        </Grid>
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Back
          </Button>
          <Stack direction="row" spacing={2} justifyContent="space-between">
            <Box fontSize="1.25rem" fontWeight="bold">
              Total Jars: {inventoryQuantityOnHand.get(PackagingType.JAR)?.toFixed(0) || 0}
            </Box>
            <Box fontSize="1.25rem" fontWeight="bold">
              Total Pouches: {inventoryQuantityOnHand.get(PackagingType.POUCH)?.toFixed(0) || 0}
            </Box>
            <Box fontSize="1.25rem" fontWeight="bold">
              Total Bulk: {inventoryQuantityOnHand.get(PackagingType.BULK)?.toFixed(3) || 0} lbs
            </Box>
          </Stack>
        </Stack>
      </Box>
    </PageContainer >
  );
}
